<template>
  <div>
    <el-form :inline="true" size="small">
      <el-form-item label="月份：">
        <el-date-picker v-model="month" type="month" value-format="yyyy-MM"
                        :clearable="false"
                        style="width:130px" @change="load" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" size="mini" plain @click="toExport">导出</el-button>
        <span class="tip">周按周一所在月归属；点数字可看对应客户；「措施」列点击可编辑；<b>还没开始的周不出数</b>（显示 —），措施可以提前填</span>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="rows" border
              size="small"
              :row-class-name="rowClass">
      <el-table-column label="时间" align="center" width="170" fixed>
        <template slot-scope="{row}">
          <b v-if="row.monthTotal">{{ row.month }} 月合计</b>
          <span v-else>
            {{ row.weekStart }} ~ {{ row.weekEnd }}
            <!-- 标明"还没到"，否则一行 — 看着像数据缺失 -->
            <el-tag v-if="row.notStarted" type="info" size="mini">未开始</el-tag>
          </span>
        </template>
      </el-table-column>
      <el-table-column label="3.0以上客户数量" align="center" width="110">
        <template slot="header">
          3.0以上客户数量
          <el-tooltip content="截至该周周日已设为大客户的主账号数；子账号并入主账号不单独计" placement="top"><i class="el-icon-question" /></el-tooltip>
        </template>
        <template slot-scope="{row}"><span :class="{ 'not-started': row.notStarted }">{{ row.notStarted ? '—' : row.bigCount }}</span></template>
      </el-table-column>
      <el-table-column label="已服务数量" align="center" width="100">
        <template slot="header">
          已服务数量
          <el-tooltip content="本月 1 号到该周周日有签到的大客户数（含子账号签到），月内逐周累计" placement="top"><i class="el-icon-question" /></el-tooltip>
        </template>
        <template slot-scope="{row}">
          <metric-cell :row="row" metric="served" :value="row.servedCount" @open="openDetail" />
        </template>
      </el-table-column>
      <el-table-column label="未服务" align="center" width="80">
        <template slot-scope="{row}">
          <metric-cell :row="row" metric="unserved" :value="row.notStarted ? null : row.bigCount - row.servedCount" @open="openDetail" />
        </template>
      </el-table-column>
      <el-table-column label="服务率" align="center" width="80">
        <template slot-scope="{row}"><span :class="{ 'not-started': row.notStarted }">{{ row.notStarted ? '—' : row.serviceRate + '%' }}</span></template>
      </el-table-column>
      <el-table-column label="加入后平均月业绩（全量）" align="center">
        <el-table-column label="低于2.5w" align="center" width="90">
          <template slot-scope="{row}"><metric-cell :row="row" metric="lowAll25" :value="row.lowAll25" @open="openDetail" /></template>
        </el-table-column>
        <el-table-column label="低于1w" align="center" width="90">
          <template slot-scope="{row}"><metric-cell :row="row" metric="lowAll10" :value="row.lowAll10" @open="openDetail" /></template>
        </el-table-column>
      </el-table-column>
      <el-table-column label="加入后平均月业绩（不含联创/IP）" align="center">
        <el-table-column label="低于2.5w" align="center" width="90">
          <template slot-scope="{row}"><metric-cell :row="row" metric="lowEx25" :value="row.lowEx25" @open="openDetail" /></template>
        </el-table-column>
        <el-table-column label="低于1w" align="center" width="90">
          <template slot-scope="{row}"><metric-cell :row="row" metric="lowEx10" :value="row.lowEx10" @open="openDetail" /></template>
        </el-table-column>
      </el-table-column>
      <!-- 余额没有历史快照：历史周这两列显示的也是**当前**余额。不写明测试会拿上周的行去对上周的余额 -->
      <el-table-column align="center">
        <template slot="header">
          余额（当前实时值，历史周同）
          <el-tooltip content="user_wallet 当前余额，没有历史快照，所以历史周这两列也是当前值" placement="top"><i class="el-icon-question" /></el-tooltip>
        </template>
        <el-table-column label="超过15w" align="center" width="90">
          <template slot-scope="{row}"><metric-cell :row="row" metric="balanceGt15" :value="row.balanceGt15" @open="openDetail" /></template>
        </el-table-column>
        <el-table-column label="超过10w" align="center" width="90">
          <template slot-scope="{row}"><metric-cell :row="row" metric="balanceGt10" :value="row.balanceGt10" @open="openDetail" /></template>
        </el-table-column>
      </el-table-column>
      <el-table-column label="措施" align="left" min-width="220">
        <template slot-scope="{row}">
          <div v-if="!row.monthTotal" class="measure" @click="editMeasure(row)">
            <span v-if="row.measure" class="measure-text">{{ row.measure }}</span>
            <span v-else class="measure-empty">点击填写</span>
            <i class="el-icon-edit" />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 点开某一格：该周该指标对应的客户 -->
    <el-dialog :title="detail.title" :visible.sync="detail.visible" width="90%"
               top="5vh"
               append-to-body>
      <!-- 提醒口径差：周报按「主账号 + 名下子账号合算」判定，这张表里每行的业绩是该客户**自己**的，
           所以点开「低于2.5w」看到的行，业绩列可能不是周报判定时用的那个数（自己的 ≤ 合算的） -->
      <div class="dialog-tip">共 {{ detail.rows.length }} 人。周报按「主账号 + 名下子账号合算」判定，本表业绩列是该客户自己的，两者不必相等。</div>
      <big-detail-table :rows="detail.rows" :loading="detail.loading" max-height="600" />
    </el-dialog>

    <!-- 措施编辑 -->
    <el-dialog :title="'措施：' + measure.weekStart + ' ~ ' + measure.weekEnd" :visible.sync="measure.visible" width="520px" append-to-body>
      <el-input v-model="measure.text" type="textarea" :rows="5"
                maxlength="1000"
                show-word-limit placeholder="本周针对未服务 / 低业绩 / 高余额客户的措施" />
      <span slot="footer">
        <el-button size="small" @click="measure.visible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="measure.saving" @click="saveMeasure">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import bigCustomerBoardApi from '@/api/count/bigCustomerBoard'
import bigDetailTable from './bigDetailTable.vue'

const METRIC_TITLES = {
  served: '已服务',
  unserved: '未服务',
  lowAll25: '平均月业绩低于 2.5w（全量）',
  lowAll10: '平均月业绩低于 1w（全量）',
  lowEx25: '平均月业绩低于 2.5w（不含联创/IP）',
  lowEx10: '平均月业绩低于 1w（不含联创/IP）',
  balanceGt15: '余额超过 15w',
  balanceGt10: '余额超过 10w'
}

// 可点开的数字：月合计行只是各周的汇总/截面，不可点（后端 weekDetail 只按周算）
const MetricCell = {
  functional: true,
  props: { row: Object, metric: String, value: Number },
  render(h, { props, listeners }) {
    // 还没开始的周：服务端不给数（notStarted=true，各指标为 null）。
    // 显示 — 而不是 0：0 的含义是"发生过且为 0"，这里是"还没发生"。
    // 也不能点 —— 点开明细那边同样没有对应客户。
    if (props.row.notStarted || props.value === null || props.value === undefined) {
      return h('span', { class: 'not-started' }, '—')
    }
    if (props.row.monthTotal) {
      return h('span', props.value)
    }
    return h('a', { class: 'metric-link', on: { click: () => listeners.open(props.row, props.metric) } }, props.value)
  }
}

/**
 * 「3.0 交付周报」（REQ-2026-107511）
 * 一行一个自然周（周一~周日，按周一所在月归属）+ 月合计行；计数只算主账号，子账号并入
 */
export default {
  components: { bigDetailTable, MetricCell },
  data() {
    return {
      month: this.dayjs().format('YYYY-MM'),
      rows: [],
      loading: false,
      detail: { visible: false, title: '', rows: [], loading: false },
      measure: { visible: false, weekStart: '', weekEnd: '', text: '', saving: false }
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      bigCustomerBoardApi.weekly(this.month).then(({ data }) => {
        this.rows = data || []
      }).catch(() => { this.rows = [] }).finally(() => { this.loading = false })
    },
    rowClass({ row }) {
      return row.monthTotal ? 'total-row' : ''
    },
    openDetail(row, metric) {
      this.detail.title = row.weekStart + ' ~ ' + row.weekEnd + ' · ' + (METRIC_TITLES[metric] || metric)
      this.detail.rows = []
      this.detail.visible = true
      this.detail.loading = true
      bigCustomerBoardApi.weekDetail(row.weekStart, metric).then(({ data }) => {
        this.detail.rows = data || []
      }).finally(() => { this.detail.loading = false })
    },
    editMeasure(row) {
      this.measure = { visible: true, weekStart: row.weekStart, weekEnd: row.weekEnd, text: row.measure || '', saving: false }
    },
    saveMeasure() {
      this.measure.saving = true
      bigCustomerBoardApi.saveMeasure(this.measure.weekStart, this.measure.text).then(res => {
        if (res.code === '0') {
          const row = this.rows.find(r => r.weekStart === this.measure.weekStart && !r.monthTotal)
          if (row) row.measure = this.measure.text.trim() || null
          this.measure.visible = false
          this.$message.success('已保存')
        }
      }).finally(() => { this.measure.saving = false })
    },
    toExport() {
      this.$router.push('/export/list?code=EEForBigCustomerWeekly&month=' + this.month)
    }
  }
}
</script>

<style scoped>
.dialog-tip {
  margin-bottom: 8px;
  color: #909399;
  font-size: 12px;
}
.tip {
  margin-left: 12px;
  color: #909399;
  font-size: 12px;
}
.measure {
  cursor: pointer;
  min-height: 20px;
  white-space: pre-wrap;
  word-break: break-all;
}
.measure-empty {
  color: #C0C4CC;
}
.not-started {
  color: #C0C4CC;
}
.measure .el-icon-edit {
  margin-left: 4px;
  color: #909399;
}
</style>
<style>
/* 周报数字链接与月合计行加粗（非 scoped：functional 组件与表格行 class 不带 scope id） */
.metric-link {
  color: #409EFF;
  cursor: pointer;
}
.metric-link:hover {
  text-decoration: underline;
}
.el-table .total-row {
  font-weight: bold;
  background: #fafafa;
}
</style>
